// ProductCardWidget.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import Skeleton from 'react-loading-skeleton';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { generateLinkWithLang } from '../../../../../utils/linkGenerator';
import CustomButton from '../../../../general/button';
import { useDispatch, useSelector } from 'react-redux';
import useCart from '../../../../../hooks/cart/useCart';
import { openSheet } from '../../../../../store/sheetToggler';
import { Builder } from '@builder.io/react';
import { selectProducts } from '../../../../../store/products';
import getSymbolFromCurrency from 'currency-symbol-map';
import { selectSelectedCurrency } from '../../../../../store/user';
import useHandleAddProduct from '../../../../../utils/handleAddProduct';
import IsGTMScriptLoaded from '../../../../../utils/isGTMScriptLoaded';
import { shouldTrackTikTokEvent } from '../../../../../utils/checkDomain';
import { ProductBoxCart } from '../../../../../pages/home/components/productBoxCart';

const ProductCardWidget = ({
  creator = '',
  name = '',
  alt,
  buttonLabel,
  isVisibleDescription = true,
  redirectUrl,
  descriptionTag = 'div',
  nameTag = 'div',
  removeDataLayer = false
}) => {
  const reduxProducts = useSelector(selectProducts);
  const product = useMemo(() => {
    if (!creator) return null;
    for (const category of reduxProducts) {
      for (const sub of category.sub_categories) {
        for (const prod of sub.products) {
          if (prod.creator === creator && Number(prod.status) === 1) {
            return prod;
          }
        }
      }
    }
    // Fallback if not found
    return { creator, name };
  }, [reduxProducts, creator, name]);

  // Debug: Show the product object as JSON
  // Remove or comment out in production
  const debugProductJson = <pre style={{fontSize: '10px', color: '#888', background: '#f5f5f5', padding: '4px', marginBottom: '8px', overflowX: 'auto'}}>{JSON.stringify(product, null, 2)}</pre>;
  const selectedCurrencyStore = useSelector(selectSelectedCurrency);
  const [selectedCurrency, setSelectedCurrency] = useState(selectedCurrencyStore || 'AUD');

  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const [showMore, setShowMore] = useState(false);
  const [isOverflow, setIsOverflow] = useState(false);
  const ref = useRef(null);

  const dispatch = useDispatch();
  const { handleAddToGiving: handleAddToGivingCart, handleCheckProduct, handleRemoveProduct} = useCart();

  const handleHeight = () => {
    setShowMore(!showMore);
  };

  const handleAddProduct = product => {
    handleAddToGivingCart(product);
    dispatch(openSheet());
  };

  useEffect(() => {
    if (product) {
      handleIsAnyAmount(product);
      if (ref.current && ref.current.clientHeight > 60) {
        setIsOverflow(true);
      }
    }
    // Include dependencies that affect the rendering of the ref'd component
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, ref.current]);

  const redirect = redirectUrl
    ? redirectUrl.replace(/\s+/g, '-').toLowerCase()
    : (product?.name || '').replace(/\s+/g, '-').toLowerCase();

  const handleViewItemDataLayer = (tempProduct, currency) => {
    const dataLayerTemp = {
      event: 'view_item',
      ecommerce: {
        currency: currency.toUpperCase(),
        items: [
          {
            item_id: tempProduct.creator,
            item_name: tempProduct.name,
            item_brand: tempProduct.category || '',
            item_category: tempProduct.category || '',
            item_description: tempProduct.description || '',
            price: tempProduct[currency.toLowerCase()],
          },
        ],
      },
    };
    window.dataLayer = window.dataLayer || [];
    if (IsGTMScriptLoaded()) {
      window.dataLayer.push(dataLayerTemp);
    }
    const obj = {
      content_type: 'product',
      content_id: tempProduct.creator,
      currency: currency,
      value: tempProduct[currency.toLowerCase()],
      price: tempProduct[currency.toLowerCase()],
      quantity: 1,
      content_category: tempProduct.category || 'donation',
      content_name: tempProduct.name,
      description: tempProduct.description,
    };
    if (window.ttq) {
      if (shouldTrackTikTokEvent()) {
        window.ttq.instance(localStorage.getItem('TikTokID')).track('ViewContent', obj);
      }
    }
  };

  const handleIsAnyAmount = product => {
    const hasQuantity = product.quantity ? product.quantity > 1 : false;
    return Number(product.aud) <= 2 && !hasQuantity;
  };

  const ShowCurrencies = (currency, _price) => {
    const tempCurrency = currency?.toUpperCase();
    let price = Number(_price);
    const options = {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    };
    if (price && Number(price) % 1 !== 0) {
      price = Number(price).toLocaleString('en', options);
    }
    let final = '';
    price = price ? price.toLocaleString() : '0';
    switch (tempCurrency) {
      case 'GBP':
      case 'EUR':
        final = `${getSymbolFromCurrency(tempCurrency.toLowerCase())}${price}`;
        break;
      case 'MYR':
        final = `${price}`;
        break;
      case 'IDR':
        final = `Rp${price}`;
        break;
      case 'AED':
        final = `Dh${price}`;
        break;
      default:
        final = `$${price}`;
        break;
    }
    return final;
  };

  useEffect(() => {
    setSelectedCurrency(selectedCurrencyStore || 'AUD');
  }, [selectedCurrencyStore]);

  return (
    // <>
    //   {debugProductJson}
    
    <>
    <div className="w-full shadow-lg bg-white pb-3 md:pb-5 h-full hidden sm:block">
      {/* Image Section */}
      <div
        className="overflow-hidden img-hover-zoom w-full tooltip [&>span]:!block cursor-pointer"
        onClick={() => {
            product.product_type ? navigate(generateLinkWithLang(product.short_description, lang)) :
                navigate(generateLinkWithLang(`/appeals/${redirect}`, lang));
        }}
      >
        {product.image_link ? (
          <LazyLoadImage
            alt={alt || product.alt || product.name}
            effect="blur"
            width="100%"
            delayTime={500}
            afterLoad={() => {
              !removeDataLayer && handleViewItemDataLayer(product, selectedCurrency);
            }}
            className="cursor-pointer block"
            onError={({ currentTarget }) => {
              currentTarget.onerror = null;
              currentTarget.src = '/images/general/logo-replace.png';
            }}
            src={product.image_link}
          />
        ) : (
          <img className="w-full" src="/images/general/logo-replace.png" alt="Placeholder" />
        )}
      </div>

      {/* Product Name */}
      <div
        className={`text-[#00a3da] cursor-pointer flex justify-center items-center text-sm sm:text-md md:text-md lg:text-lg xl:text-xl text-center px-1 mt-3 min-h-[56px] ${
          isVisibleDescription ? 'md:mb-2 xs:mb-2' : 'xs:mb-2 md:mb-0'
        } mt-2 leading-4 md:leading-none uppercase ${lang === 'ar' ? 'font-[AlmariMedium] font-bold' : 'font-medium'}`}
        onClick={() => {
          if (product.product_type) {
            navigate(generateLinkWithLang(product.short_description, lang));
          } else {
            navigate(generateLinkWithLang(`/appeals/${redirect}`, lang));
          }
        }}
      >
        {React.createElement(nameTag, {
          dangerouslySetInnerHTML: {
            __html: product.alternativeName || product.name || <Skeleton height={56} />,
          }
        })}
      </div>

      {/* Description Section */}
      <div className="relative text-center">
        {(isVisibleDescription || showMore) && (
          <div
            className={`transition-all duration-300 text-center my-2 relative overflow-hidden ${
              showMore ? 'h-auto mb-4' : 'max-h-[65px] md:max-h-[78px] mb-0'
            }`}
          >
            {React.createElement(descriptionTag, {
              className: `text-stone-500 text-sm sm:text-sm md:text-base lg:text-base overflow-hidden transition-all duration-200 min-h-[65px] md:min-h-[79px] px-2 md:px-3 ${
                lang === 'ar' ? 'font-[AlmariMedium]' : ''
              }`,
              ref:ref,
              dangerouslySetInnerHTML: {
                __html: product.product_type
                  ? product.description
                  : product.short_description || product.description,
              }
            })}
          </div>
        )}
        {(isOverflow || !isVisibleDescription) && (
          <ChevronDownIcon
            onClick={handleHeight}
            className={`md:block w-6 h-6 text-[#00a3da] transition-all duration-300 cursor-pointer mx-auto ${showMore ? 'transform rotate-180' : ''}`}
          />
        )}
      </div>

      {/* Pricing and Button */}
      <div className="grid grid-cols-6 gap-1 2xl:gap-2">
        <div  className={`text-[#f60362] ${product.product_type ? "invisible" : ''} col-span-6 md:text-xl text-center font-bold ${lang === 'ar' ? 'font-[AlmariMedium]' : ''}`}>
        { handleIsAnyAmount(product) ? (
          lang === 'ar'
            ? 'أي مبلغ'
            : lang === 'fr'
            ? "N'importe quel montant"
            : lang === 'es'
            ? 'Cualquier cantidad'
            : 'Any Amount'
        ) : (
          <div className="flex items-center justify-center">
            <div>
              {ShowCurrencies(
                selectedCurrency,
                product[selectedCurrency.toLowerCase()] * (product.quantity || 1),
                true
              )}
            </div>
            <span className="text-xs ml-0.5">{selectedCurrency}</span>
          </div>
        )}
        </div>
        <div className="flex justify-center col-span-6">
          <CustomButton
            
            onClick={() => {
              if (product.product_type) {
                navigate(generateLinkWithLang(product.short_description, lang));
              } else {
                handleAddProduct(product);
              }
            }}
            title={
              buttonLabel ||
              (product.product_type
                ? product.product_type
                : lang === 'ar'
                ? 'تبرع الان'
                : lang === 'fr'
                ? 'Faites un don maintenant'
                : lang === 'es'
                ? 'Dona ahora'
                : 'Donate Now')
            }
            className={`mx-auto ${lang === 'ar' ? 'font-[AlmariMedium]' : ''} !rounded-none`}
          />
        </div>
      </div>
    </div>
    <div className="sm:hidden">
    <ProductBoxCart
        product={product}
        onCheck={handleCheckProduct}
        onAdd={handleAddToGivingCart}
        onRemove={handleRemoveProduct}
        currency={selectedCurrency}
        stateVariableObj={{ productName: product.name }}
        _lang={'eng'}
        linkBtnClasses={product.linkBtnClasses || ''}
        isStaticProduct={['Zakat Calculator'].includes(product.product_type)}
        staticLink={product.product_type ? product.short_description : ''}
        staticLinkText={product.product_type ? product.name : ''}
    />
    </div>
    </>
  );
};

Builder.registerComponent(ProductCardWidget, {
  name: 'ProductCardWidget',
  inputs: [
    {
      name: 'creator',
      type: 'string',
      required: true,
      helperText: 'Product creator ID (e.g. QB-25-001)',
    },
    {
      name: 'name',
      type: 'string',
      helperText: 'Override product name',
    },
    // You can add more props here if needed
  ],
});

export default ProductCardWidget;
